/**
 * Pure formatting and string utility functions.
 *
 * Extracted from auth.js — no global state dependencies.
 *
 * Usage:
 *   import { capitalize, formatPhone, formatSSN } from './helpers/string-utils.js';
 */

import { isJMS, isSheetz } from './client-flags.js';

export function getCertNumber(string) {
	if (!string || isJMS) return '';
	let match;
	const clean = string.replace(/,/g, '');
	// Sheetz certs may appear as CB-1994 or #CB-1994
	if (isSheetz) match = clean.match(/(?:#)?([A-Za-z]{1,5}-\d{1,20})(?!\w)/i);
	else match = clean.match(/#([A-Za-z0-9\/:|._-]{1,20})(?!\w)/);
	if (match) match[2] = match[1]; // ensure cert number is always in match[2]
	return match || '';
}

/**
 * Compute display initials for a full name, honoring a client's configured
 * overrides (e.g. nicknames used in transaction notes, like "Max" for
 * "Maximilian"). Falls back to the first letter of the first two words.
 * A name with no space (e.g. a raw email address echoed back by a failed
 * name lookup) yields a single letter, which is the deliberate signal that
 * the input wasn't a real name.
 * @param {string} fullName
 * @param {Object} [overrides] - Map of full name -> initials override
 * @returns {string}
 */
export function getInitials(fullName, overrides = {}) {
	if (!fullName) return '';
	if (overrides[fullName]) return overrides[fullName];

	let names = fullName.split(' ');
	let initials = names[0].substring(0, 1).toUpperCase();
	if (names.length > 1) initials += names[1].substring(0, 1).toUpperCase();
	return initials;
}

export function capitalize(string) {
	if (!string) return null;
	return string.charAt(0).toUpperCase() + string.slice(1);
}

export function singular(string) {
	if (!string) return string;
	const lower = string.toLowerCase();
	// "Series" / "species" — already singular-shaped
	if (/(series|species)$/i.test(string)) return string;
	// "Entities" → "Entity", "Properties" → "Property"
	if (lower.endsWith('ies') && string.length > 3)
		return string.slice(0, -3) + (string[string.length - 3] === string[string.length - 3].toUpperCase() ? 'Y' : 'y');
	// "Classes" → "Class", "Boxes" → "Box", "Bushes" → "Bush", "Watches" → "Watch"
	if (/(s|x|ch|sh)es$/i.test(string)) return string.slice(0, -2);
	// "Funds" → "Fund", "Shares" → "Share"
	if (lower.endsWith('s') && !lower.endsWith('ss')) return string.slice(0, -1);
	return string;
}

export function formatPhone(phone) {
	return phone
		.replace(/\+1/g, '')
		.replace(/\D+/g, '')
		.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
}

export function formatSSN(ssn) {
	let val = ssn.replace(/[^\d-]/g, '');
	val = val.replace(/^(\d{3})-?(\d{1,2})/, '$1-$2');
	val = val.replace(/^(\d{3})-?(\d{2})-?(\d{1,4})/, '$1-$2-$3');
	val = val
		.split('')
		.filter((val, idx) => {
			return val != '-' || idx == 3 || idx == 6;
		})
		.join('');
	return val.substring(0, 11);
}

export function formatEIN(ein) {
	let val = ein.replace(/[^\d-]/g, '');
	val = val.replace(/^(\d{2})-?(\d{1,7})/, '$1-$2');
	val = val
		.split('')
		.filter((val, idx) => {
			return val != '-' || idx == 2;
		})
		.join('');
	return val.substring(0, 10);
}

export function maskTaxIdLast4(encValue, entityType) {
	const digits = String(encValue || '').replace(/\D/g, '');
	const t = (entityType || '').toLowerCase().trim();
	const last4 = digits.slice(-4);
	if (!last4) return '';

	const isSsnEntityType = !t || t === 'individual' || t === 'rev_trust' || t === 'custodial';
	if (isSsnEntityType) return `***-**-${last4}`;
	else return `**-***${last4}`;
}

export function stripSubAddress(emailAddress) {
	const regex = /^(.*?)\+.*?(@.*)$/;
	return emailAddress.replace(regex, '$1$2');
}

export function itsTempAddress(emailAddress) {
	const prefixes = [
		'temp-',
		'temp_',
		'block-',
		'block_',
		'departed-',
		'departed_',
		'no-access-',
		'no_access_',
		'corporate-',
		'corporate_',
		'terminated-',
		'terminated_',
	];
	return prefixes.some((prefix) => emailAddress.startsWith(prefix));
}

export function cleanStampedName(fileName) {
	const replacements = {
		',': '_',
		' ': '_',
		'\u00A0': '_',
		'`': "'",
		'\u2018': "'",
		'\u2019': "'",
		'&': 'AND',
		'#': 'HASH_',
		'+': 'PLUS',
		'%': 'PCT',
		'\u2013': '-',
		'/': '-',
		'\\': '-',
	};

	let stampedName = Date.now() + '_' + fileName.replace(/[,`\u2018\u2019\u00A0\s&#+%\u2013\/\\]/g, (match) => replacements[match] ?? '_').replace(/_+/g, '_');

	return stampedName;
}

export function formatImg(img, transformation) {
	if (img == undefined || img.length < 49) return '';
	let transform = transformation;
	let start = img.slice(0, 49);
	let end = img.slice(49);
	img = (start + transform + end).replace(/\/\//g, '/').replace(/:\//g, '://');
	return img;
}

export function formatPreview(img, transformation, preview) {
	if (preview) img = preview;
	let transform = transformation;
	let start = img.slice(0, 49);
	let end = img.slice(49, img.length - 4);
	img = start + transform + end + '.png';
	return img;
}

export function uuidv4() {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
		let r = (Math.random() * 16) | 0,
			v = c == 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

export function getCAGR(oldValue, newValue, oldTime, newTime) {
	const SECONDS_PER_YEAR = 365.25 * 24 * 60 * 60;
	if (oldTime > 1e12) oldTime = Math.floor(oldTime / 1000);
	if (newTime > 1e12) newTime = Math.floor(newTime / 1000);
	const yearsBetween = (newTime - oldTime) / SECONDS_PER_YEAR;
	if (oldValue <= 0 || yearsBetween <= 0) return null;
	return Math.pow(newValue / oldValue, 1 / yearsBetween) - 1;
}

export function sortByFFM(advisedData) {
	const key = (r) =>
		(Array.isArray(r?.tags) ? r.tags : [])
			.map((t) => (t && t.tag ? t.tag : t))
			.filter(Boolean)
			.slice(0, 4)
			.map((s) => String(s).trim().toLowerCase())
			.join('|');

	return advisedData
		.map((r, i) => ({ r, i, k: key(r) }))
		.sort((a, b) => {
			const aEmpty = a.k === '',
				bEmpty = b.k === '';
			if (aEmpty && !bEmpty) return 1;
			if (!aEmpty && bEmpty) return -1;
			return a.k.localeCompare(b.k) || a.i - b.i;
		})
		.map((x) => x.r);
}

// Functional helpers
export const arr = (x) => Array.from(x);
export const num = (x) => Number(x) || 0;
export const str = (x) => String(x);
export const isEmpty = (xs) => xs.length === 0;
export const take = (n) => (xs) => xs.slice(0, n);
export const drop = (n) => (xs) => xs.slice(n);
export const reverse = (xs) => xs.slice(0).reverse();
export const comp = (f) => (g) => (x) => f(g(x));
export const not = (x) => !x;
export const chunk = (n) => (xs) => (isEmpty(xs) ? [] : [take(n)(xs), ...chunk(n)(drop(n)(xs))]);

export let numToWords = (n) => {
	let a = [
		'',
		'one',
		'two',
		'three',
		'four',
		'five',
		'six',
		'seven',
		'eight',
		'nine',
		'ten',
		'eleven',
		'twelve',
		'thirteen',
		'fourteen',
		'fifteen',
		'sixteen',
		'seventeen',
		'eighteen',
		'nineteen',
	];
	let b = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
	let g = ['', 'thousand', 'million', 'billion', 'trillion', 'quadrillion', 'quintillion', 'sextillion', 'septillion', 'octillion', 'nonillion'];
	let makeGroup = ([ones, tens, huns]) => {
		return [num(huns) === 0 ? '' : a[huns] + ' hundred ', num(ones) === 0 ? b[tens] : (b[tens] && b[tens] + '-') || '', a[tens + ones] || a[ones]].join('');
	};
	let thousand = (group, i) => (group === '' ? group : `${group} ${g[i]}`);
	if (typeof n === 'number') return numToWords(String(n));
	else if (n === '0') return 'zero';
	else return comp(chunk(3))(reverse)(arr(n)).map(makeGroup).map(thousand).filter(comp(not)(isEmpty)).reverse().join(' ');
};

export function decimalToWords(d) {
	let normalized = parseFloat(Number(d).toFixed(6));
	let decimal = normalized.toString().split('.')[1];
	if (decimal) {
		const cleanedDecimal = decimal.replace(/0+$/, '');
		function convertToWords(numberString) {
			const digits = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
			return numberString
				.split('')
				.map((digit) => digits[digit])
				.join(' ');
		}
		return numToWords(Math.trunc(normalized)) + ' point ' + convertToWords(cleanedDecimal);
	} else {
		return numToWords(normalized);
	}
}

export function stringifyNumber(num) {
	const dayWords = [
		'first',
		'second',
		'third',
		'fourth',
		'fifth',
		'sixth',
		'seventh',
		'eighth',
		'ninth',
		'tenth',
		'eleventh',
		'twelfth',
		'thirteenth',
		'fourteenth',
		'fifteenth',
		'sixteenth',
		'seventeenth',
		'eighteenth',
		'nineteenth',
		'twentieth',
	];
	let n = Number(num) - 1;
	if (n < 20) return capitalize(dayWords[n]);
	else if (n < 30) return 'Twenty-' + capitalize(dayWords[n % 10]);
	else if (n == 30) return 'Thirtieth';
	else return 'Thirty-First';
}

export function evaluateAsFloat(rawText) {
	if (!rawText) return '';
	rawText = rawText.replace(/[^-+*/.\d]/g, '');
	let lastChar = rawText.slice(-1);
	switch (lastChar) {
		case '+':
			rawText += '0';
			break;
		case '-':
			rawText += '0';
			break;
		case '*':
			rawText += '1';
			break;
		case '/':
			rawText += '1';
			break;
	}
	let variables = Object.create(null);
	function evaluate(obj) {
		switch (obj.type) {
			case 'number':
				return parseFloat(obj.value.replace(/,/g, ''));
			case 'name':
				return variables[obj.id] || 0;
			case '+':
				return evaluate(obj.left) + evaluate(obj.right);
			case '-':
				return evaluate(obj.left) - evaluate(obj.right);
			case '*':
				return evaluate(obj.left) * evaluate(obj.right);
			case '/':
				return evaluate(obj.left) / evaluate(obj.right);
		}
	}
	let answer = Math.round(Number(evaluate(parseForFloat(rawText)) * 100000000)) / 100000000;
	return answer;

	function parseForFloat(text) {
		let position = 0;
		let tokens = tokenize(text);
		function tokenize(cleanText) {
			let results = [];
			let tokenRegExp = /\s*([+-]?[0-9,.]+|[A-Za-z]+|\S)\s*/g;
			let m;
			while ((m = tokenRegExp.exec(cleanText)) !== null) results.push(m[1]);
			return results;
		}
		function peek() {
			return tokens[position];
		}
		function consume(token) {
			position++;
		}
		function isNumber(token) {
			return token !== undefined && token.match(/^[+-]?[0-9,.]+$/) !== null;
		}
		function isName(token) {
			return token !== undefined && token.match(/^[A-Za-z]+$/) !== null;
		}
		function parsePrimaryExpr() {
			let t = peek();
			if (isNumber(t)) {
				consume(t);
				return { type: 'number', value: t };
			} else if (isName(t)) {
				consume(t);
				return { type: 'name', id: t };
			} else if (t === '(') {
				consume(t);
				let expr = parseExpr();
				if (peek() !== ')') throw new SyntaxError('expected )');
				consume(')');
				return expr;
			} else {
				throw new SyntaxError('expected a number, a variable, or parentheses');
			}
		}
		function parseMulExpr() {
			let expr = parsePrimaryExpr();
			let t = peek();
			while (t === '*' || t === '/') {
				consume(t);
				let rhs = parsePrimaryExpr();
				expr = { type: t, left: expr, right: rhs };
				t = peek();
			}
			return expr;
		}
		function parseExpr() {
			let expr = parseMulExpr();
			let t = peek();
			while (t === '+' || t === '-') {
				consume(t);
				let rhs = parseMulExpr();
				expr = { type: t, left: expr, right: rhs };
				t = peek();
			}
			return expr;
		}
		let result = parseExpr();
		if (position !== tokens.length) throw new SyntaxError("unexpected '" + peek() + "'");
		return result;
	}
}

export function getAllUrlParams(url, lower) {
	let queryString = url ? url.split('?')[1] : window.location.search.slice(1);
	let obj = {};
	if (queryString) {
		queryString = queryString.split('#')[0];
		let arr = queryString.split('&');
		for (let i = 0; i < arr.length; i++) {
			let a = arr[i].split('=');
			let paramName = a[0];
			let paramValue = typeof a[1] === 'undefined' ? true : a[1];
			if (lower) {
				paramName = paramName.toLowerCase();
				if (typeof paramValue === 'string') paramValue = paramValue.toLowerCase();
			}
			if (paramName.match(/\[(\d+)?\]$/)) {
				let key = paramName.replace(/\[(\d+)?\]/, '');
				if (!obj[key]) obj[key] = [];
				if (paramName.match(/\[\d+\]$/)) {
					let index = /\[(\d+)\]/.exec(paramName)[1];
					obj[key][index] = paramValue;
				} else {
					obj[key].push(paramValue);
				}
			} else {
				if (!obj[paramName]) {
					obj[paramName] = paramValue;
				} else if (obj[paramName] && typeof obj[paramName] === 'string') {
					obj[paramName] = [obj[paramName]];
					obj[paramName].push(paramValue);
				} else {
					obj[paramName].push(paramValue);
				}
			}
		}
	}
	return obj;
}

export function splitAddressFields(addressStr) {
	// Centralized address splitter (trusts the string; no geocode)
	// Handles:
	//   "123 Main St, Boston, MA 02111"
	//   "123 Main St, Suite 200, Boston, MA 02111-1234"
	//   "... , UNITED STATES" (country peeled off)
	// ALSO handles common 2-line format:
	//   "594 North 850 East\nAmerican Fork, UT 84003"
	//
	// Patch: handles missing comma between City and ST:
	//   "7605 E Pearson St, Tucson AZ 85715" -> "7605 E Pearson St, Tucson, AZ 85715"
	//
	// Returns:
	//   { ok, address1, address2, city, state, zip, country, raw }

	const raw = (addressStr || '').toString().trim();
	if (!raw) return { ok: false, address1: '', address2: '', city: '', state: '', zip: '', country: '', raw: '' };

	const empty = { ok: false, address1: '', address2: '', city: '', state: '', zip: '', country: '', raw };

	const parseStateZip = (s) => {
		s = (s || '').trim();

		// 1) Try standard: "UT 84664" | "UT 84664-1234" | "UT"
		let m = s.match(/^([A-Za-z]{2})(?:\s+(\d{5}(?:-\d{4})?))?$/);
		if (m) {
			return { state: (m[1] || '').toUpperCase(), zip: m[2] || '' };
		}

		// 2) Try full state name: "Utah 84664" | "Utah"
		m = s.match(/^([A-Za-z][A-Za-z\s\.]+?)(?:\s+(\d{5}(?:-\d{4})?))?$/);
		if (m) {
			const stateNameRaw = (m[1] || '').replace(/\./g, '').trim().toLowerCase();
			const zip = m[2] || '';

			const STATE_TO_ABBR = {
				alabama: 'AL',
				alaska: 'AK',
				arizona: 'AZ',
				arkansas: 'AR',
				california: 'CA',
				colorado: 'CO',
				connecticut: 'CT',
				delaware: 'DE',
				florida: 'FL',
				georgia: 'GA',
				hawaii: 'HI',
				idaho: 'ID',
				illinois: 'IL',
				indiana: 'IN',
				iowa: 'IA',
				kansas: 'KS',
				kentucky: 'KY',
				louisiana: 'LA',
				maine: 'ME',
				maryland: 'MD',
				massachusetts: 'MA',
				michigan: 'MI',
				minnesota: 'MN',
				mississippi: 'MS',
				missouri: 'MO',
				montana: 'MT',
				nebraska: 'NE',
				nevada: 'NV',
				'new hampshire': 'NH',
				'new jersey': 'NJ',
				'new mexico': 'NM',
				'new york': 'NY',
				'north carolina': 'NC',
				'north dakota': 'ND',
				ohio: 'OH',
				oklahoma: 'OK',
				oregon: 'OR',
				pennsylvania: 'PA',
				'rhode island': 'RI',
				'south carolina': 'SC',
				'south dakota': 'SD',
				tennessee: 'TN',
				texas: 'TX',
				utah: 'UT',
				vermont: 'VT',
				virginia: 'VA',
				washington: 'WA',
				'west virginia': 'WV',
				wisconsin: 'WI',
				wyoming: 'WY',
				'district of columbia': 'DC',
			};

			const abbr = STATE_TO_ABBR[stateNameRaw] || '';
			return { state: abbr, zip };
		}

		return { state: '', zip: '' };
	};

	// ------------------------------------------------------------
	// 0) First pass: preserve line breaks and handle 2-line format
	// ------------------------------------------------------------
	{
		const lines = raw
			.split(/\r?\n+/)
			.map((s) => s.trim())
			.filter(Boolean);

		// Most common: 2 lines: street on line1, "City, ST ZIP" on line2
		if (lines.length >= 2) {
			// Strip trailing country line if present (3rd line etc.)
			let country = '';
			const lastLine = lines[lines.length - 1] || '';
			if (lines.length >= 3 && /^(united states|united states of america|usa|u\.s\.a\.|us|u\.s\.)$/i.test(lastLine)) {
				country = lastLine;
				lines.pop();
			}

			const l1 = lines[0] || '';
			const l2 = lines[1] || '';

			let city = '';
			let state = '';
			let zip = '';

			const parts2 = l2
				.split(',')
				.map((p) => p.trim())
				.filter(Boolean);

			if (parts2.length >= 2) {
				city = parts2[0] || '';
				const sz = parseStateZip(parts2.slice(1).join(' ').trim());
				state = sz.state;
				zip = sz.zip;
			} else {
				// No comma: assume last tokens are state/zip; city is the rest
				// e.g., "American Fork UT 84003"
				const m = l2.match(/^(.*)\s+([A-Za-z]{2})(?:\s+(\d{5}(?:-\d{4})?))?\s*$/);
				if (m) {
					city = (m[1] || '').trim();
					state = (m[2] || '').toUpperCase();
					zip = m[3] || '';
				}
			}

			if (state || zip) {
				const ok = !!(l1 || city || state || zip || country);
				return {
					ok,
					address1: l1,
					address2: '',
					city,
					state,
					zip,
					country,
					raw,
				};
			}
		}
	}

	// ------------------------------------------------------------
	// 1) Comma-based logic
	// ------------------------------------------------------------

	let normalized = raw.replace(/\s+/g, ' ').trim();

	// Peel off country if present as a trailing comma-part
	let country = '';
	{
		const parts0 = normalized
			.split(',')
			.map((p) => p.trim())
			.filter(Boolean);
		const last = parts0[parts0.length - 1] || '';
		if (/^(united states|united states of america|usa|u\.s\.a\.|us|u\.s\.)$/i.test(last)) {
			country = last;
			parts0.pop();
			normalized = parts0.join(', ');
		}
	}

	// ------------------------------------------------------------
	// PATCH: fix missing comma between City and ST in 1-line addresses
	// Examples:
	//   "..., Tucson AZ 85715"  -> "..., Tucson, AZ 85715"
	//   "..., American Fork UT 84003" -> "..., American Fork, UT 84003"
	// Only when it looks like ", <city> <ST> <ZIP>" at the end.
	// ------------------------------------------------------------
	{
		const m = normalized.match(/^(.*?,)\s*([^,]+?)\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/);
		if (m) {
			const head = (m[1] || '').trim(); // includes trailing comma
			const cityMaybe = (m[2] || '').trim();
			const st = (m[3] || '').toUpperCase();
			const z = m[4] || '';

			// Guard: avoid accidentally rewriting things like "Suite AZ 85715" as a city.
			// Require city to have at least 2 letters total.
			const letterCount = (cityMaybe.match(/[A-Za-z]/g) || []).length;
			if (letterCount >= 2) {
				normalized = `${head} ${cityMaybe}, ${st} ${z}`.replace(/\s+/g, ' ').trim();
			}
		}
	}

	let parts = normalized
		.split(',')
		.map((p) => p.trim())
		.filter(Boolean);

	// Handle 4-segment US addresses: "City, ST, ZIP" -> merge "ST ZIP"
	if (parts.length >= 4) {
		const last = parts[parts.length - 1];
		const prev = parts[parts.length - 2];

		const isZipOnly = /^\d{5}(?:-\d{4})?$/.test(last);
		const isStateOnly = /^[A-Za-z]{2}$/.test(prev);

		if (isZipOnly && isStateOnly) {
			parts.splice(parts.length - 2, 2, `${prev} ${last}`);
		}
	}

	let address1 = '';
	let address2 = '';
	let city = '';
	let state = '';
	let zip = '';

	if (parts.length >= 3) {
		const streetParts = parts.slice(0, -2);
		city = parts[parts.length - 2] || '';
		const sz = parseStateZip(parts[parts.length - 1] || '');
		state = sz.state;
		zip = sz.zip;

		if (streetParts.length) {
			const lastStreet = streetParts[streetParts.length - 1] || '';

			if (/^(apt|apartment|unit|ste|suite|#)\b/i.test(lastStreet)) {
				address2 = lastStreet;
				address1 = streetParts.slice(0, -1).join(', ');
			} else {
				address1 = streetParts.join(', ');
			}
		}
	} else if (parts.length === 2) {
		const sz = parseStateZip(parts[1] || '');
		if (sz.state || sz.zip) {
			state = sz.state;
			zip = sz.zip;

			const p0 = parts[0] || '';
			const tokens = p0.split(' ').filter(Boolean);

			if (tokens.length >= 4) {
				const city2 = tokens.slice(-2).join(' ');
				const addr2 = tokens.slice(0, -2).join(' ');
				if (addr2.length >= 6) {
					address1 = addr2;
					city = city2;
				} else {
					city = tokens.slice(-1).join(' ');
					address1 = tokens.slice(0, -1).join(' ');
				}
			} else {
				city = p0;
			}
		} else {
			city = parts[0] || '';
			state = sz.state;
			zip = sz.zip;
		}
	} else {
		address1 = parts[0] || normalized;
	}

	const ok = !!(address1 || city || state || zip || country);
	return { ok, address1, address2, city, state, zip, country, raw };
}

export function normalizeCertNumber(certNumber) {
	if (!certNumber) return '';
	return String(certNumber).replace(/,\s*/g, ' / ');
}

/** Prefix values so Excel/ExcelJS treat certificate and ID columns as text, not numbers. */
export function formatExcelTextCell(value) {
	if (value === null || value === undefined || value === '') return '';
	const str = String(value).trim();
	if (!str) return '';
	return str.startsWith('\u200B') ? str : '\u200B' + str;
}

export function parseCertNumbersFromDescription(description) {
	if (!description) return { senderCert: '', receiverCert: '' };

	// Two forms:
	// "Transfer (N from M)"   — no hash: receiver=N, sender=M
	// "Transfer (#N from ...)" — hash: sender=N, receiver extracted from after "from"
	const match = String(description).match(/Transfer\s*\(\s*(#?)(\d+)\s+from\s+(.+?)\s*\)/i);
	if (!match) return { senderCert: '', receiverCert: '' };

	const hasHash = match[1] === '#';
	const firstCert = match[2];
	const fromPart = match[3].trim();

	if (hasHash) {
		// Sender is the hash-prefixed cert; receiver comes from the "from" clause
		const issuanceMatch = fromPart.match(/^Issuance\s+(\d+)/i);
		const receiverCert = issuanceMatch ? issuanceMatch[1] : fromPart.replace(/\s+\d{1,2}\/\d{1,2}\/\d{2,4}\s*$/, '').trim();
		return { senderCert: firstCert, receiverCert };
	}

	// No hash: before "from" is receiver, after "from" is sender
	return { senderCert: fromPart, receiverCert: firstCert };
}

export function cleanCSV(rawText) {
	let results = Papa.parse(rawText);
	let data = results.data;

	// Clean cell content: trim whitespace and convert newlines
	for (let i = 0; i < data.length; i++) {
		for (let j = 0; j < data[i].length; j++) {
			data[i][j] = (data[i][j] || '').replace(/[\n\r]/g, ' ').trim();
		}
	}

	// Remove rows that are entirely blank
	let goodRows = data.filter((row) => row.some((cell) => (cell || '').trim() !== ''));

	// Remove columns that are entirely blank
	let goodCols = (goodRows[0] || []).map((_, i) => goodRows.some((row) => (row[i] || '').trim() !== ''));

	let trimmed = goodRows.map((row) => row.filter((_, i) => goodCols[i]));

	// Optional: remove trailing empty rows again (safety)
	while (trimmed.length && trimmed[trimmed.length - 1].every((cell) => (cell || '').trim() === '')) {
		trimmed.pop();
	}

	// Optional: remove trailing empty columns again (safety)
	if (trimmed.length) {
		let lastRow = trimmed[trimmed.length - 1];
		while (lastRow.length && trimmed.every((row) => (row[row.length - 1] || '').trim() === '')) {
			for (let row of trimmed) row.pop();
			lastRow = trimmed[trimmed.length - 1];
		}
	}

	return trimmed;
}

export function secureFormData(res) {
	const formData = new FormData();
	if (res.data.fields) {
		Object.entries(res.data.fields).forEach(([key, value]) => {
			formData.append(key, value);
		});
	}
	return formData;
}
